import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { skipToken } from '@tanstack/react-query';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  AssessmentOutcomeSchema,
  type AssessmentOutcome,
} from '@duke-hq/svc-api-db/schemas/enums/AssessmentOutcome.schema';
import { t } from '@lingui/core/macro';

import type { AssessmentSubmission, SelectableOutcome } from '../types';
import { needsRevisionFeedback } from '../utils';

function narrowOutcome(value: AssessmentOutcome | undefined) {
  return value === AssessmentOutcomeSchema.enum.SATISFACTORY
    ? AssessmentOutcomeSchema.enum.SATISFACTORY
    : AssessmentOutcomeSchema.enum.NOT_YET_SATISFACTORY;
}

export function useOverviewSubmissionState({
  assessmentSubmission,
  enabled,
}: {
  assessmentSubmission: AssessmentSubmission;
  enabled: boolean;
}) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const assessmentSubmissionId = assessmentSubmission.id;
  const shouldFetchOutcome =
    enabled && !!assessmentSubmissionId && !assessmentSubmission.assessed_at;

  const { data: outcomeData, isFetching: isOutcomeQueryFetching } =
    trpcAdmin.assessments.getCalculatedOutcome.useQuery(
      shouldFetchOutcome ? { assessmentSubmissionId } : skipToken,
      { refetchOnMount: 'always' },
    );

  const { data: wouldCompleteCourseData, isFetching: isWouldCompleteQueryFetching } =
    trpcAdmin.assessments.getWouldCompleteCourse.useQuery(
      shouldFetchOutcome
        ? {
            unitAssessmentId: assessmentSubmission.unit_assessment_id,
            unitAssessmentEnrolmentId: assessmentSubmission.unit_assessment_enrolment_id,
          }
        : skipToken,
    );

  const isLoadingOutcome = isOutcomeQueryFetching || isWouldCompleteQueryFetching;
  const calculatedOutcome = narrowOutcome(outcomeData?.calculatedOutcome);

  const [feedback, setFeedbackState] = useState<string>('');
  const [reason, setReasonState] = useState<string>('');
  const [selectedOutcome, setSelectedOutcome] = useState<SelectableOutcome>(calculatedOutcome);
  const [isConfirmingOutcomeUpdate, setIsConfirmingOutcomeUpdate] = useState(false);
  const [pendingOutcomeData, setPendingOutcomeData] = useState<AssessmentOutcome | null>(null);
  const [lastSyncedOutcome, setLastSyncedOutcome] = useState<SelectableOutcome>(calculatedOutcome);

  if (calculatedOutcome !== lastSyncedOutcome) {
    setLastSyncedOutcome(calculatedOutcome);
    setSelectedOutcome(calculatedOutcome);
  }

  const setFeedback = useCallback(
    (input: string | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setFeedbackState(typeof input === 'string' ? input : input?.target?.value);
    },
    [],
  );
  const setReason = useCallback(
    (input: string | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setReasonState(typeof input === 'string' ? input : input?.target?.value);
    },
    [],
  );

  const canOverride = calculatedOutcome === AssessmentOutcomeSchema.enum.NOT_YET_SATISFACTORY;
  const isOverriding = canOverride && selectedOutcome === AssessmentOutcomeSchema.enum.SATISFACTORY;

  const hasSlideMissingRevisionFeedback =
    assessmentSubmission.assessorSlides.some(needsRevisionFeedback);

  const isValid = useMemo(() => {
    if (hasSlideMissingRevisionFeedback) {
      return false;
    }
    if (isOverriding) {
      return feedback.trim().length > 0 && reason.trim().length > 0;
    }
    return feedback.trim().length > 0;
  }, [hasSlideMissingRevisionFeedback, isOverriding, feedback, reason]);

  const isCourseEnrolmentCompleted =
    assessmentSubmission.unitAssessmentEnrolment.courseEnrolment.status === 'COMPLETED';
  const willCompleteCourseWhenSatisfactory = wouldCompleteCourseData?.wouldCompleteCourse ?? false;

  const { mutate: submitAssessmentFeedback, isPending: isSubmitting } =
    trpcAdmin.assessments.submitAssessmentFeedback.useMutation({
      onSettled: async (_data, error) => {
        if (error) {
          toast({
            status: 'error',
            title: t`Error submitting assessment`,
            description: error.message,
          });
          return;
        }
        toast({
          status: 'success',
          title: t`Assessment submitted successfully.`,
          description: t`The assessment outcome and feedback have been recorded.`,
        });
        await utils.assessments.all.invalidate(undefined, { refetchType: 'active' });
        await utils.assessments.snapshot.refetch();
        await utils.assessments.getById.invalidate();
        await utils.assessments.getAllByAssessmentEnrolmentId.refetch();
        await utils.assessments.getCalculatedOutcome.invalidate();
        await utils.assessments.getWouldCompleteCourse.invalidate();
      },
    });

  const submitFeedback = useCallback(
    (outcome: AssessmentOutcome) => {
      if (assessmentSubmission.id == null || !assessmentSubmission.unit_assessment_enrolment_id) {
        return;
      }
      submitAssessmentFeedback({
        id: assessmentSubmission.id,
        outcome,
        feedback,
        unitAssessmentEnrolmentId: assessmentSubmission.unit_assessment_enrolment_id,
        reason: reason || undefined,
      });
    },
    [assessmentSubmission, submitAssessmentFeedback, feedback, reason],
  );

  const handleAssessment = useCallback(
    (outcome: AssessmentOutcome) => {
      const needsConfirmation =
        (isCourseEnrolmentCompleted || willCompleteCourseWhenSatisfactory) &&
        outcome === AssessmentOutcomeSchema.enum.SATISFACTORY;
      if (needsConfirmation || isCourseEnrolmentCompleted) {
        setPendingOutcomeData(outcome);
        setIsConfirmingOutcomeUpdate(true);
      } else {
        submitFeedback(outcome);
      }
    },
    [isCourseEnrolmentCompleted, willCompleteCourseWhenSatisfactory, submitFeedback],
  );

  const handleConfirmOutcomeUpdate = useCallback(() => {
    if (pendingOutcomeData) {
      submitFeedback(pendingOutcomeData);
      setIsConfirmingOutcomeUpdate(false);
      setPendingOutcomeData(null);
    }
  }, [pendingOutcomeData, submitFeedback]);

  const closeConfirmationDialog = useCallback(() => {
    setIsConfirmingOutcomeUpdate(false);
    setPendingOutcomeData(null);
  }, []);

  const handleSubmit = useCallback(() => {
    handleAssessment(selectedOutcome);
  }, [handleAssessment, selectedOutcome]);

  return useMemo(
    () => ({
      feedback,
      setFeedback,
      reason,
      setReason,
      selectedOutcome,
      setSelectedOutcome,
      canOverride,
      isOverriding,
      isValid,
      calculatedOutcome,
      isLoadingOutcome,
      isSubmitting,
      handleSubmit,
      isConfirmingOutcomeUpdate,
      closeConfirmationDialog,
      handleConfirmOutcomeUpdate,
      pendingOutcomeData,
      willCompleteCourseWhenSatisfactory,
      isCourseEnrolmentCompleted,
    }),
    [
      feedback,
      setFeedback,
      reason,
      setReason,
      selectedOutcome,
      setSelectedOutcome,
      canOverride,
      isOverriding,
      isValid,
      calculatedOutcome,
      isLoadingOutcome,
      isSubmitting,
      handleSubmit,
      isConfirmingOutcomeUpdate,
      closeConfirmationDialog,
      handleConfirmOutcomeUpdate,
      pendingOutcomeData,
      willCompleteCourseWhenSatisfactory,
      isCourseEnrolmentCompleted,
    ],
  );
}

export type OverviewSubmissionState = ReturnType<typeof useOverviewSubmissionState>;
